import { getExternalVATRates } from 'client/actions/agerasExternal'
import { getCustomSettings } from 'client/actions/customSettings'
import { getExperiments } from 'client/actions/experiments'
import { loadPayNowAuthorizationStatus, savePayNowAuthorization } from 'client/actions/payNowManagement'
import { getCustomerById } from 'client/api/customer'
import { parsePropsFromQueryParams } from 'client/components/paynow/Paynow.helper'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { InvoiceTypeInterface } from 'client/Interfaces/InvoiceTypeInterface'
import { PayNowAuthorizationSucess } from 'client/Interfaces/PayNow/AccountAuthorizationParams'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { l } from 'client/Other/Translation'
import { INITIAL_STATE } from 'client/reducers/customSettings'
import { getTopMatchingIbanFields } from 'client/reducers/draft'
import * as _ from 'lodash'
import moment from 'moment'
import * as React from 'react'
import { withRouter } from 'react-router-dom'

import { getAccounts } from '../../actions/accounts'
import { getCompany } from '../../actions/company'
import { getConfigs } from '../../actions/config'
import { getCustomers, setReceiver } from '../../actions/customer'
import * as DocumentNumberActions from '../../actions/documentNumber'
import {
  addProductLine,
  changeDraft,
  changeFields,
  changeLines,
  checkSubscriptionAndOpenDownpaymentModal,
  checkSubscriptionAndOpenPaymentScheduleModal,
  deleteOrder,
  draftSaveAndClose,
  initDraft,
  previewDraft,
  showRecurringProfileIntermediaryModal,
  updateDraftCustomer,
  updateDraftStylesFromSettings,
  updateOrder,
  updateValidationError,
  updateValidationField,
  validationError,
} from '../../actions/draft'
import { closeEditor } from '../../actions/editorActions'
import { previewDraft as previewEstimateDraft } from '../../actions/estimateDraft'
import { getExchangeRate } from '../../actions/exchangeRate'
import { getFreeTexts } from '../../actions/freeTexts'
import { getTranslations } from '../../actions/language'
import { navigateAddModal, navigateRemoveModal, setNavigation } from '../../actions/navigation'
import { getAllProducts } from '../../actions/products'
import { deleteRecurringProfileSettings, updateRecurringProfileSettings } from '../../actions/recurringProfile'
import { getSettings } from '../../actions/settings'
import { Analytics } from '../../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result, trackingFields } from '../../Global/AnalyticsEvents'
import { Controller } from '../../Global/Controller'
import { isInvoiceTypeWithMandatoryVatColumn } from '../../Global/InvoiceTypes'
import { CompanyInterface } from '../../Interfaces/CompanyInterface'
import { CustomerInterface } from '../../Interfaces/CustomerInterface'
import { DocumentTypes, DraftInterface, InvoiceTypes } from '../../Interfaces/Draft/DraftInterface'
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface'
import { LineInterface } from '../../Interfaces/Draft/LineInterface'
import { OrderDraftInterface } from '../../Interfaces/OrderInterface'
import {
  RecurringProfileSettingsInterface,
  RecurringProfileStatusType,
} from '../../Interfaces/RecurringProfileInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'
import { QuotaTypes } from '../../Interfaces/Subscription'
import { getCurrencies, getCurrencySymbol, getInvoiceTypes, getLocales } from '../../Other/Configs'
import { countriesWithinEU } from '../../Other/Countries'
import { isDateStringValid } from '../../Other/functions/dateFunctions'
import { Immutable } from '../../Other/Immutable'
import { Templates } from '../../Other/Templates'
import { hasModuleAvailable, moduleNames } from '../../reducers/settings'
import {
  doesCurrentPlanSupportFeature,
  getCompanySubscriptionSelector,
} from '../../reducers/Subscription/companySubscription'
import { notifier } from '../common/Notifier'
import { TABS } from '../Modal/CompanyInformationModal/CompanyInformationModal'
import { InvoiceEditor as IE, IEExternalActions, IEStateProps } from './InvoiceEditor'
import { getDeductionFromInvoiceType } from './utils/getDeductionFromInvoiceType'
import { getInvoiceTypesMap } from './utils/getInvoiceTypesMap'

export const EXTRA_COLUMNS = ['taxTotal', 'netTotal', 'index', 'vatPct']
const EXCLUDED_COLUMNS = ['description', 'discountPct']

const isNonNegInteger = (s: string): boolean => {
  const n = Number(s)
  return String(n) === s && n >= 0 && n === Math.floor(n)
}
const isNonEmptyStr = (s: any): boolean => typeof s === 'string' && s !== ''
const isValidDocNumber = (s: any): boolean => isNonEmptyStr(s) && isNonNegInteger(s)

const getInvalidFields = (draft: DraftInterface, company: CompanyInterface): string[] => {
  let invalidFields = draft?.invalidFields ? [...draft.invalidFields] : []
  const validationFields = draft.validationFields?.required || []
  if (draft.document === DocumentTypes.ESTIMATE && !isValidDocNumber(draft.fields.invoiceNumber.value)) {
    invalidFields.push('documentNumber')
  }
  if (!draft.receiver || !draft.receiver.id) {
    invalidFields.push('customer')
  } else {
    invalidFields = _.without(invalidFields, 'customer')
  }
  if (
    draft.document === DocumentTypes.INVOICE &&
    validationFields.includes('recipientReference') &&
    !draft.fields.recipientReference.value
  ) {
    invalidFields.push('recipientReference')
  }
  if (_.isEmpty(draft.fields.issueDate.value) || !isDateStringValid(draft.fields.issueDate.value)) {
    invalidFields.push('issueDate')
  }
  if (_.isEmpty(draft.fields.dueDate.value) || !isDateStringValid(draft.fields.dueDate.value)) {
    invalidFields.push('dueDate')
  }
  if (company?.agerasNextMigration) {
    if (!draft.receiver?.externalContactId) {
      invalidFields.push('contactId')
    } else {
      invalidFields = _.without(invalidFields, 'contactId')
    }
  }

  invalidFields = invalidFields.reduce((acc, curr) => {
    if (!/line-\d+-/.test(curr)) {
      acc.push(curr)
    }

    return acc
  }, [])

  if (draft.document === DocumentTypes.INVOICE && !_.isEmpty(draft.lines) && Array.isArray(draft.lines)) {
    draft.lines.forEach((line, index) => {
      if (line.type === 'row' && !line.productType) {
        invalidFields.push(`line-${index}-productType`)
      }
      if (company?.agerasNextMigration && line.type === 'row' && line?.columns?.includes('vatPct') && !line.vatRateId) {
        invalidFields.push(`line-${index}-vatRateId`)
      }
    })
  }

  return invalidFields
}

export const getInvalidFieldsExcludeDiscount = (draft, dispatch, state): string[] => {
  const invalidFields = getInvalidFields(draft, state?.company)
  dispatch(updateValidationError(invalidFields))

  return invalidFields
    ?.sort()
    ?.filter((item, pos, ary) => !pos || item != ary[pos - 1])
    ?.filter((f) => f !== 'discount')
}

export const getInvalidRecurringProfileFields = (profile: RecurringProfileSettingsInterface): string[] => {
  const invalidFields = []
  const today = moment().startOf('day')
  if (
    profile.status !== RecurringProfileStatusType.ACTIVE &&
    (!profile.startDate || !moment(profile.startDate).isAfter(today))
  ) {
    invalidFields.push('startDate')
  }
  return invalidFields
}

const closeModal = (dispatch, modalName: string) => () => dispatch(navigateRemoveModal(modalName))

const openModal = (dispatch, modalName: string) => () => {
  return dispatch(
    navigateAddModal({
      component: modalName,
      props: { close: closeModal(dispatch, modalName) },
    }),
  )
}

export const refetchUnsyncedCustomerData = async (
  draft: DraftInterface,
  state,
  dispatch,
): Promise<CustomerInterface | void> => {
  if (state?.company?.agerasNextMigration && draft?.receiver && !draft?.receiver?.externalContactId) {
    const customer = await getCustomerById(draft.receiver.id)
    await dispatch(updateDraftCustomer(customer))
    return customer
  }
}

export const getInvoiceType = (state: StateInterface): InvoiceTypeInterface | {} => {
  const fields = state.draft.fields
  const typeFromSettings = fields?.type ? fields.type.value.toString() : false
  const invoiceType = getInvoiceTypes(state.config, state.company.country, state.draft.document).find(
    (it) => it.id.toString() === typeFromSettings,
  )
  return invoiceType || {}
}

// adjust the draft if needed, meant for cases where corrupted drafts
// might've been already saved and there's no other single place
// where we can fix them when previewing or saving again
const getAdjustedDraft = (draft: DraftInterface, state: StateInterface): DraftInterface => {
  const invoiceType = getInvoiceType(state)
  if (
    draft.fields &&
    !_.includes(draft.fields.columns.value, 'vatPct') &&
    isInvoiceTypeWithMandatoryVatColumn(invoiceType)
  ) {
    draft.fields.columns.value.push('vatPct')
  }

  return draft
}

const getUpdatedFields = (fields, value: number) => {
  return {
    ...fields,
    type: { ...fields.type, value: Number(value) },
    deduction: { ...fields.deduction, ...getDeductionFromInvoiceType(Number(value)) },
    discountLocation: {
      name: 'discountLocation',
      value: [10, 11].includes(Number(value)) ? 'perLine' : fields.discountLocation.value,
    },
  }
}

const mapDispatchToProps = (dispatch): IEExternalActions => {
  return {
    onDocumentNumberEdit: () => dispatch(DocumentNumberActions.openInvoiceNumberEditModal()),
    getCustomers: () => dispatch(getCustomers()),
    initDraft: () => dispatch(initDraft({ document: DocumentTypes.INVOICE })),
    getCompany: () => dispatch(getCompany()),
    getSettings: () => dispatch(getSettings()),
    getProducts: () => dispatch(getAllProducts()),
    getCustomSettings: () => dispatch(getCustomSettings()),
    getExperiments: () => dispatch(getExperiments()),
    getConfigs: () => dispatch((dispatch, getState) => getConfigs(getState().config)),
    getAccounts: () => dispatch((dispatch, getState) => getAccounts(getState().accounts)),
    getFreetexts: () => dispatch(getFreeTexts()),
    getTranslations: (lang: string) => dispatch(getTranslations(lang)),
    changeLines: (lines: LineInterface[]) => dispatch(changeLines(lines)),
    changeDraft: (draft: DraftInterface) => {
      dispatch(changeDraft(draft))
    },
    changeCustomer: (customer: any) => dispatch(updateDraftCustomer(customer)),
    changeFields: async (fields: DraftFieldsInterface) => {
      await dispatch(changeFields(fields))
      dispatch((dispatch, getState) => {
        dispatch(updateValidationError(getInvalidFields(getState().draft, getState().company)))
      })
    },
    changeStyles: (settings: SettingsInterface) => dispatch(updateDraftStylesFromSettings(settings)),
    previewDraft: async (origDraft: DraftInterface) => {
      dispatch(async (dispatch, getState) => {
        const draft = getAdjustedDraft(origDraft, getState())
        // Validate draft for accounting and fetch customers list for contact id
        const refetchedCustomer = await refetchUnsyncedCustomerData(draft, getState(), dispatch)
        // After refetching the customer data we need to use fresh draft state that has the client externalId updated
        const invalidFields: string[] = getInvalidFieldsExcludeDiscount(getState().draft, dispatch, getState())
        dispatch(validationError(invalidFields))
        if (invalidFields.length) {
          Analytics.trackEvent(`${Events.previewDocument}`, {
            type: AnalyticsType.action,
            category: Category.draft,
            result: Result.error,
            placement: Placement.editor,
            documentType: draft.document,
            invalidFields,
          })
          if (draft?.receiver && window?.initialConfiguration?.eInvoiceOnboardingEnabled) {
            dispatch(updateValidationField(draft.receiver))
          }
          const isContactSelected = Boolean(draft?.fields?.customer?.value)
          if (invalidFields.includes('contactId') && isContactSelected) {
            notifier('CustomerNotFullySynced', 'warning')
            Segment.track(SegmentEvents.debug.invoiceCreation, {
              customer_id: draft?.receiver?.id || '',
              customer_external_id: refetchedCustomer?.externalContactId || '',
            })
          }
          return
        }
        Analytics.trackEvent(`${Events.previewDocument}`, {
          type: AnalyticsType.action,
          category: Category.draft,
          result: Result.success,
          placement: Placement.editor,
          documentType: draft.document,
        })
        if (draft.invoicer.tradeName.logo?.content) {
          draft.invoicer.tradeName.logo.content = null
        }
        if (draft.document === 'invoice' || draft.document === 'creditnote') {
          dispatch(previewDraft(draft.id))
        } else if (draft.document === 'estimate') {
          dispatch(previewEstimateDraft(draft.id))
        }
      })
    },
    previewRecurringProfile: async (draft: DraftInterface, profile: RecurringProfileSettingsInterface) => {
      dispatch(async (dispatch, getState) => {
        // Validate draft for accounting and fetch customers list for contact id
        await refetchUnsyncedCustomerData(draft, getState(), dispatch)
        const invalidDraftFields: string[] = getInvalidFieldsExcludeDiscount(draft, dispatch, getState())
        const invalidProfileFields: string[] = getInvalidRecurringProfileFields(profile)
        const invalidFields = invalidDraftFields.concat(invalidProfileFields)
        if (invalidFields.length) {
          dispatch(validationError(invalidFields))
          Analytics.trackEvent(`${Events.previewRecurringInvoice}`, {
            type: AnalyticsType.action,
            category: Category.recurringInvoice,
            result: Result.error,
            placement: Placement.editor,
          })
          if (draft?.receiver && invalidFields.includes('contactId')) {
            notifier('CustomerNotFullySynced', 'warning')
          }
          return
        }
        Analytics.trackEvent(`${Events.previewRecurringInvoice}`, {
          type: AnalyticsType.action,
          category: Category.recurringInvoice,
          result: Result.success,
          placement: Placement.editor,
        })
        if (draft.invoicer.tradeName.logo?.content) {
          draft.invoicer.tradeName.logo.content = null
        }
        dispatch(previewDraft(draft.id))
      })
    },
    draftSaveAndClose: async (origDraft) => {
      dispatch(async (dispatch, getState) => {
        const draft = getAdjustedDraft(origDraft, getState())

        // Validate draft for accounting and fetch customers list for contact id
        await refetchUnsyncedCustomerData(draft, getState(), dispatch)
        const invalidFields: string[] = getInvalidFieldsExcludeDiscount(draft, dispatch, getState())
        if (invalidFields.length) {
          dispatch(validationError(invalidFields))
          Analytics.trackEvent(`${Events.previewDocument}`, {
            type: AnalyticsType.action,
            category: Category.draft,
            result: Result.error,
            placement: Placement.editor,
            documentType: draft.document,
            invalidFields,
          })
          if (window.initialConfiguration.eInvoiceOnboardingEnabled) {
            dispatch(updateValidationField(draft?.receiver))
          }
          // For Next users: customer without contact id
          if (draft?.receiver && invalidFields.includes('contactId')) {
            notifier('CustomerNotFullySynced', 'warning')
          }
          return
        }
        await draftSaveAndClose(draft, dispatch)
      })
    },
    getExchangeRate: () => dispatch(getExchangeRate()),
    openCustomerModal: (customer: CustomerInterface, suggestedCustomerName: string) => {
      const eventname = customer ? trackingFields.name.editCustomer : trackingFields.name.addCustomer
      Analytics.trackEvent(eventname, {
        type: trackingFields.type.action,
        category: trackingFields.category.customer,
        result: trackingFields.result.open,
        placement: trackingFields.placement.editor,
      })
      dispatch(
        navigateAddModal({
          component: 'CustomerModal',
          props: {
            isOpen: true,
            close: () => {
              dispatch(navigateRemoveModal('CustomerModal'))
            },
            customer,
            suggestedCustomerName,
            source: 'editor',
            next: (customer: CustomerInterface) => {
              dispatch(setReceiver(customer))
              dispatch(updateDraftCustomer(customer))
            },
          },
        }),
      )
    },
    openDownpaymentModal: () => dispatch(checkSubscriptionAndOpenDownpaymentModal()),
    updateOrder: (order: OrderDraftInterface) => dispatch(updateOrder(order)),
    deleteOrder: () => dispatch(deleteOrder()),
    updateRecurringProfileSettings: (profile: RecurringProfileSettingsInterface) =>
      dispatch(updateRecurringProfileSettings(profile)),
    deleteRecurringProfileSettings: () => dispatch(deleteRecurringProfileSettings()),
    openPaymentScheduleModal: () => dispatch(checkSubscriptionAndOpenPaymentScheduleModal()),
    openRecurringProfileIntermediaryModal: () => dispatch(showRecurringProfileIntermediaryModal()),
    openProductModal: (index, context) =>
      dispatch(
        navigateAddModal({
          component: 'ProductModal',
          props: {
            isOpen: true,
            next: (product) => {
              dispatch(addProductLine({ index, product }))
              dispatch(getAllProducts())
            },
            context,
          },
        }),
      ),
    openDefaultProductTypeModal: (index, onClose) =>
      dispatch(
        navigateAddModal({
          component: 'DefaultProductTypeModal',
          props: {
            isOpen: true,
            onClose,
            index,
          },
        }),
      ),
    openVATOptions: (afterCreateNew) =>
      dispatch(
        navigateAddModal({
          component: 'VATModal',
          props: {
            isOpen: true,
            afterCreateNew,
          },
        }),
      ),
    openUnitOptions: (afterCreateNew) =>
      dispatch(
        navigateAddModal({
          component: 'UnitModal',
          props: {
            isOpen: true,
            afterCreateNew,
          },
        }),
      ),
    openPaymentTermsModal: (afterCreateNew) =>
      dispatch(
        navigateAddModal({
          component: 'PaymentTermsModal',
          props: {
            isOpen: true,
            afterCreateNew,
          },
        }),
      ),
    openLatePaymentInterestModal: (afterCreateNew) =>
      dispatch(
        navigateAddModal({
          component: 'LatePaymentInterestModal',
          props: {
            isOpen: true,
            afterCreateNew,
          },
        }),
      ),
    closeEditor: () => {
      dispatch(closeEditor())
    },
    navigateToInvoicingSettings: () => {
      dispatch((dispatch, getState) => {
        dispatch(
          setNavigation({
            path: '/myAccount/invoicingSettings',
            app: {
              ...getState().navigation.app,
              mod: 'settings',
              view: 'invoicingSettings',
            },
          }),
        )
      })
    },
    updatePaymentMeans: (draft, newIbanFields) => {
      dispatch((dispatch, getState) => {
        const { paymentMethods } = getState().settings.values
        const paymentMeans = Immutable.copy(paymentMethods)
        const iban = paymentMeans.find(({ type }) => type === 'IBAN')
        iban.fields = newIbanFields
        dispatch(
          changeDraft({
            ...draft,
            paymentMeans,
          }),
        )
      })
    },
    navigateToCompanySettings: (draft, activeTab, tradeNameId?) => {
      dispatch((dispatch, getState) => {
        Analytics.trackEvent(Events.editSettings, {
          type: AnalyticsType.modal,
          category: Category.setting,
          result: Result.open,
          placement: Placement.editor,
          tab: activeTab || TABS.companyDetails,
        })
        dispatch(
          navigateAddModal({
            component: 'CompanyInformationModal',
            props: {
              activeTab: activeTab || TABS.companyDetails,
              tradeNameId: tradeNameId || draft.invoicer.tradeName._id,
              originUrl: '/reactInvoice',
              originDocumentType: draft.document,
              next: () => {
                const { company: stateCompany, settings } = getState()
                const { vatNumber = '', businessId = '' } = stateCompany
                const { paymentMethods, company } = getState().settings.values
                const paymentMeans = Immutable.copy(paymentMethods)
                const { tradeNames, ...companySettings } = company
                const tradeName =
                  tradeNames.find((t) => t._id === (tradeNameId || draft.invoicer.tradeName._id)) || tradeNames[0]
                const settingsIban = paymentMeans.find(({ type }) => type === 'IBAN')
                const draftIban = draft.paymentMeans.find(({ type }) => type === 'IBAN')
                if (draftIban && settingsIban) {
                  settingsIban.fields = getTopMatchingIbanFields(draftIban, getState().settings)
                }
                const draftFields = Immutable.copy<DraftFieldsInterface>(draft.fields)

                draftFields.type.value = Number(
                  getState()
                    .settings.models.find(({ type }) => type === DocumentTypes.INVOICE)
                    .fields.find(({ name }) => name === 'type').default,
                )
                dispatch(
                  changeDraft({
                    ...draft,
                    invoicer: { ...draft.invoicer, ...companySettings, vatNumber, businessId, tradeName },
                    paymentMeans,
                  }),
                )
                dispatch(changeFields(draftFields))
              },
            },
          }),
        )
      })
    },
    openPayNowFailureModal: openModal(dispatch, 'PayNowAuthorizationFailureModal'),
    openPayNowSuccessModal: openModal(dispatch, 'PayNowAuthorizationSuccessModal'),
    saveAuthorization: (authorizationParams: PayNowAuthorizationSucess) =>
      dispatch(savePayNowAuthorization(authorizationParams)),
    loadAuthorizationStatus: () => dispatch(loadPayNowAuthorizationStatus()),
    getExternalVATRates: () => dispatch(getExternalVATRates()),
  }
}

const isRecurringSelected = (state) => {
  return Boolean(
    state.draft && state.draft.fields.recurringProfileEnabled && state.draft.fields.recurringProfileEnabled.value,
  )
}

let previousCustomer

const mapStateToProps = (state: StateInterface): IEStateProps => {
  // Init the default object and return it if no draft is present
  const PROPS = {
    token: state.token,
    subscription: state.subscriptionManagement.companySubscription.subscription,
    customers: state.customers,
    products: state.products,
    drafts: {
      collection: [],
    },
    draft: state.draft,
    recurringProfile: state.recurringProfile,
    settings: state.settings,
    company: state.company,
    language: state.language,
    config: state.config,
    exchangeRate: state.exchangeRate,
    ongoing: state.ongoing,
    customSettings: state.customSettings || INITIAL_STATE,
    experiments: state.experiments,
    hasRightsToPaymentSchedules: doesCurrentPlanSupportFeature(
      getCompanySubscriptionSelector(state),
      QuotaTypes.PAYMENT_SCHEDULE,
    ),
    hasRightsToRecurringProfile: doesCurrentPlanSupportFeature(
      getCompanySubscriptionSelector(state),
      QuotaTypes.RECURRING_PROFILE,
    ),
    isRecurringProfile: Boolean(isRecurringSelected(state) && state.recurringProfile && state.recurringProfile.id),
    payNow: state.payNow,
    ...parsePropsFromQueryParams(location ? location.search : ''),
    externalVATRates: state.externalVATRates,
    isAgerasNextMigration: state.company.agerasNextMigration,
  }

  if (!PROPS.draft) {
    return PROPS
  }

  if (previousCustomer === undefined) {
    previousCustomer = PROPS.draft?.receiver
  }

  // Set the values for internal part

  const INVOICE_TYPES = (() => {
    const invoiceTypes = getInvoiceTypes(state.config, state.company.country, state.draft.document)
    const result = {}
    invoiceTypes.forEach((type) => {
      result[type.id] = l(type.translationKey)
    })
    return result
  })()

  const invoiceTypesMap = getInvoiceTypesMap(PROPS?.company, PROPS?.config, state.draft?.receiver, INVOICE_TYPES)

  const customerCountry = state.draft?.receiver?.address?.country
  const customerType = state.draft?.receiver?.customerType
  const hasCustomerChanged =
    customerCountry !== previousCustomer?.address?.country || customerType !== previousCustomer?.customerType
  const isCustomerTypeCompany = customerType === 'c'
  const isVATLiable = !state?.settings?.values?.company?.isNotVATLiable

  const FIELDS = (() => {
    let fields = state.draft.fields

    const documentType = state.draft?.document

    const shouldOverrideInvoiceTypeBasedOnCustomerCountry =
      isCustomerTypeCompany && (isVATLiable || (fields && !invoiceTypesMap[fields.type.value]))

    if (
      fields &&
      documentType === DocumentTypes.INVOICE &&
      customerCountry &&
      (hasCustomerChanged || invoiceTypesMap[fields.type.value] === undefined)
    ) {
      if (shouldOverrideInvoiceTypeBasedOnCustomerCountry) {
        if (countriesWithinEU.includes(customerCountry)) {
          const found = Object.entries(invoiceTypesMap).find(
            ([invoiceTypeId, _value]) => PROPS.config.invoiceTypes[invoiceTypeId].type === 'SALES_EU_GOODS',
          )

          if (found && found.length > 0) {
            const value = Number(found[0])
            fields = getUpdatedFields(fields, value)
          }
        } else {
          const found = Object.entries(invoiceTypesMap).find(
            ([invoiceTypeId, _value]) => PROPS.config.invoiceTypes[invoiceTypeId].type === 'SALES_NONEU_GOODS',
          )

          if (found && found.length > 0) {
            const value = Number(found[0])
            fields = getUpdatedFields(fields, value)
          }
        }
      } else {
        // override invoice type field with default invoice type from account settings
        const accountDefaultInvoiceTypeId = state?.settings?.models
          ?.find((model) => model?.type === DocumentTypes.INVOICE)
          ?.fields?.find((field) => field?.name === 'type')?.default

        if (accountDefaultInvoiceTypeId) {
          const found = Object.entries(invoiceTypesMap).find(
            ([invoiceTypeId, _value]) =>
              String(PROPS.config.invoiceTypes[invoiceTypeId].id) === String(accountDefaultInvoiceTypeId),
          )

          if (found && found.length > 0) {
            const value = Number(found[0])
            fields = getUpdatedFields(fields, value)
          }
        }
      }
    }

    if (fields && !invoiceTypesMap[fields.type.value]) {
      const firstInvoiceType = Number(Object.entries(invoiceTypesMap)[0][0])
      fields = getUpdatedFields(fields, firstInvoiceType)
    }

    return fields
  })()

  PROPS.draft.fields = { ...FIELDS }

  const country = state.token.country
  const TEMPLATES = Templates.getTemplates(country)
  const {
    template: { value: tpName },
  } = FIELDS
  const tpFromSettings = TEMPLATES.find((t) => t === tpName)

  const TEMPLATE = tpFromSettings || TEMPLATES.find((t) => t.includes('default'))
  const LANGUAGES = getLocales(state.config, state.token.country).sort((a, b) => {
    return a.localeCompare(b)
  })

  const SCHEMA = (() => {
    const schemas = Immutable.copy(state.settings.schemas)
    const {
      draft: { document },
    } = state
    return schemas[document] || schemas.invoice
  })()

  const MODEL = (() => {
    const models = Immutable.copy(state.settings.models)
    const {
      draft: { document },
    } = state
    const modelDoc = models.find((model) => model.type === document)
    const model = modelDoc || models.find((model) => model.type === DocumentTypes.INVOICE)
    const hiddenFields = ['paymentTime', 'interest', 'dueDate', 'type', 'template', 'language']
    if (!modelDoc && document === DocumentTypes.CREDITNOTE) {
      model.fields = model.fields.map((field) => {
        const visibleProp = hiddenFields.includes(field.name) ? { visible: false } : {}
        return { ...field, ...visibleProp }
      })
    }
    return model
  })()

  const INVOICE_TYPE: any = getInvoiceType(state)
  const INVOICE_FIELDS = MODEL.columns
  const visibleColumnsFromSettings = String(MODEL.fields.find((field) => field?.name === 'columns')?.default)?.split(
    ',',
  )
  const VISIBLE_COLUMNS = (() => {
    const initialColumns = hasCustomerChanged ? visibleColumnsFromSettings : FIELDS.columns.value
    FIELDS.columns.value = initialColumns.map((v) => (v === 'total' ? 'grossTotal' : v))
    const filteredColumns = FIELDS.columns.value.filter((col) => {
      if (col === 'grossTotal') {
        col = 'total'
      }
      return EXCLUDED_COLUMNS.indexOf(col) < 0 && (INVOICE_FIELDS.indexOf(col) >= 0 || EXTRA_COLUMNS.indexOf(col) > -1)
    })

    if (isInvoiceTypeWithMandatoryVatColumn(INVOICE_TYPE)) {
      filteredColumns.push('vatPct')
    }

    return filteredColumns
  })()
  const LANGUAGE = (() => {
    let lang = FIELDS?.locale?.value
    if (!lang) {
      lang = MODEL.fields.filter((f) => {
        return f.name === 'language'
      })[0].default as string
    }
    return LANGUAGES.filter((l) => {
      // Compare the language, not locale, because fr_BE != fr_FR but the language should be the same.
      return l.substr(0, 2).indexOf(lang.substr(0, 2)) === 0
    })[0]
  })()
  const LINE_COLUMNS = (() => {
    let columns = Immutable.copy(VISIBLE_COLUMNS) // FIELDS.columns.value;
    // hide or show discount column
    if (FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perLine') {
      if (columns.indexOf('discount') < 0) {
        columns.push('discount')
      }
    } else if (columns.indexOf('discount') >= 0) {
      columns = columns.filter((c) => {
        return c !== 'discount'
      })
    }
    if (columns.indexOf('description') < 0) {
      columns.unshift('description')
    }
    if (columns.indexOf('productType') < 0) {
      columns.unshift('productType')
    }
    if (INVOICE_TYPE.fields && INVOICE_TYPE.fields.includes('work')) {
      columns.push('work') // TODO: localize
    }
    if (isInvoiceTypeWithMandatoryVatColumn(INVOICE_TYPE)) {
      columns.push('vatPct')
    }
    return columns
  })()

  MODEL.fields.push({
    default: 0,
    name: 'deduction',
    visible:
      INVOICE_TYPE.fields &&
      INVOICE_TYPE.fields.includes('work') &&
      [InvoiceTypes.SALES_HOUSEHOLD_SE_ROT, InvoiceTypes.SALES_HOUSEHOLD_SE_RUT].includes(INVOICE_TYPE.id),
  })

  // if draft was saved before recurring profile release, add recurringProfile toggle to FIELDS
  if (!PROPS.draft.fields?.recurringProfileEnabled) {
    PROPS.draft.fields = {
      ...PROPS.draft.fields,
      recurringProfileEnabled: {
        name: 'recurringProfileEnabled',
        visible: hasModuleAvailable(state.settings, moduleNames.recurringProfiles),
        value: false,
      },
    }
  }

  const tradeNames = {}
  state.settings.values.company.tradeNames.forEach((tn) => {
    tradeNames[tn._id] = tn.name
  })

  const templates = {}
  TEMPLATES.forEach((t) => {
    let templateName = t
    if (t.indexOf('.') > 0) {
      templateName = l('invoice.template.' + t.substr(t.indexOf('.') + 1))
    }
    templates[t] = templateName
  })

  const currencies = {}
  const currencyArray = getCurrencies(state.config)
  currencyArray.sort((a, b) => {
    const longNameCompare = a.longName.localeCompare(b.longName)
    const shortNameCompare = a.shortName.localeCompare(b.shortName)
    return longNameCompare !== 0 ? longNameCompare : shortNameCompare
  })
  currencyArray.map((c) => {
    currencies[c.shortName] = `${c.shortName} (${c.longName})`
  })

  const CURRENCY_SYMBOL = getCurrencySymbol(state.config, PROPS.draft.fields.currency.value)

  const languages = {}

  // todo: sort languages by localized language, not by locale identifier
  LANGUAGES.map((key) => {
    languages[key] = l('language.' + key.split('_')[0])
  })

  const columnNames = {}
  INVOICE_FIELDS.forEach((col) => {
    if (EXCLUDED_COLUMNS.indexOf(col) < 0) {
      columnNames[col] = l(`invoice.line.${col}`)
    }
  })
  if (!INVOICE_TYPE && MODEL) {
    MODEL.columns.forEach((col) => {
      if (EXCLUDED_COLUMNS.indexOf(col) < 0) {
        columnNames[col] = l(`invoice.line.${col}`)
      }
    })
  }

  const TAX_FREE = VISIBLE_COLUMNS.indexOf('vatPct') < 0

  if (INVOICE_TYPE && (isInvoiceTypeWithMandatoryVatColumn(INVOICE_TYPE) || !TAX_FREE)) {
    columnNames.taxTotal = l('invoice.line.taxTotal')
  }
  columnNames.index = l('invoice.line.index')

  const DEFAULT_CURRENCY = (() => {
    return getCurrencies(state.config).find((c) => {
      return c.id === state.company.currencyId
    })?.shortName
  })()

  previousCustomer = PROPS.draft?.receiver

  return {
    ...PROPS,
    selectMaps: {
      tradeNames,
      templates,
      currencies,
      languages,
      columnNames,
      invoiceTypes: INVOICE_TYPES,
    },
    internal: {
      model: MODEL,
      schema: SCHEMA,
      language: LANGUAGE,
      template: TEMPLATE,
      visibleColumns: VISIBLE_COLUMNS,
      lineColumns: LINE_COLUMNS,
      taxFree: TAX_FREE,
      currencySymbol: CURRENCY_SYMBOL,
      defaultCurrency: DEFAULT_CURRENCY,
    },
  }
}

export const InvoiceEditor = withRouter(Controller<IEStateProps>(mapStateToProps, mapDispatchToProps)(IE))
